import React from 'react'
import { InputBase } from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './ProductQuantitySelector.messages'
import IncreaseIcon from '@mui/icons-material/Add'
import DecreaseIcon from '@mui/icons-material/Remove'

export interface IOwnProps {
  productAmount: number
  maximum: number
  onChange: (quantity: number) => void
  className?: string
}

export type IProps = IOwnProps

const QuantityControlContainer = styled('div')({
  display: 'flex',
  alignItems: 'stretch',
})

const QuantityField = styled(InputBase)({
  width: 130,
  border: '1px solid #797979',
  'input': {
    textAlign: 'center',
    MozAppearance: 'textfield',
  },
  'input::-webkit-outer-spin-button,input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
  },
})

const IncrementButton = styled(Button)({
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  fontSize: 32,
  fontWeight: 500,
})

const DecrementButton = styled(Button)({
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
  fontSize: 32,
  fontWeight: 500,
})

const QuestionContainer = styled('div')({
  fontSize: 18,
  marginBottom: 6,
  fontWeight: 600,
})

const ProductQuantitySelector: React.FC<IProps> = (props) => {
  const {
    productAmount,
    maximum,
    onChange,
    className,
  } = props

  const intl = useIntl()

  const [quantity, setQuantity] = React.useState(productAmount)

  React.useEffect(() => {
    onChange(quantity)
  }, [onChange, quantity])

  const handleChangeQuantity = (Quantity: string) => {
    const newQuantity =  getNewQuantity(Quantity, maximum)

    if (quantity !== newQuantity) {
      setQuantity(newQuantity)
    }
  }

  const handleDecreaseClick = () => {
    setQuantity(quantity - 1)
  }

  const handleIncreaseClick = () => {
    setQuantity(quantity + 1)
  }

  return (
    <div className={className}>
      <QuestionContainer>
        <FormattedMessage {...messages.questionAboutQuantityProductToDeliver} />
      </QuestionContainer>

      <QuantityControlContainer>
        <DecrementButton
          color='secondary'
          variant='contained'
          disabled={quantity === 0}
          onClick={handleDecreaseClick}
          aria-label={intl.formatMessage({...messages.decreaseButton})}
        >
          <DecreaseIcon />
        </DecrementButton>

        <QuantityField
          type='number'
          value={quantity}
          aria-label={intl.formatMessage({...messages.decreaseButton})}
          onChange={e => handleChangeQuantity(e.target.value)}
        />

        <IncrementButton
          color='secondary'
          variant='contained'
          disabled={quantity === maximum}
          onClick={handleIncreaseClick}
          aria-label={intl.formatMessage({...messages.increaseButton})}
        >
          <IncreaseIcon />
        </IncrementButton>
      </QuantityControlContainer>
    </div>
  )
}

const getNewQuantity = (newQuantity: string, max: number) => {
  const newQuantityParsed = Number(newQuantity)

  if (newQuantityParsed > max) {
    return max
  }
  if (newQuantityParsed < 0) {
    return 0
  }

  return newQuantityParsed
}

export default ProductQuantitySelector
